/**
 * Quote-part terrain strategy.
 *
 * Buyer transaction conservation (each sale delta):
 *   revenueDelta
 *     = usableSpvProceeds          (quote-part + mise en valeur net)
 *     + buyerDirectLandSettlement  (direct to land debt / bank)
 *     + buyerDirectCurrentConstruction
 *     + constructionReimbursement  (repays prior SPV-funded construction)
 *
 * SPV cash receives only: usableSpvProceeds + constructionReimbursement.
 * Land settlement and buyer-direct construction never enter SPV cash.
 */

import { n } from '../math.js';
import {
  constructionPctAtMonth,
  evaluateConstructionBankConditions,
  evaluateLandBankConditions,
} from './bankConditions.js';
import { resolveSecuredFacilities, securedBankFacilityCap } from './bankFacilities.js';

/** Pure construction share of sale price (buyer-financed directly). */
export function pureConstructionRatio(pnl) {
  const sales = Math.max(0, pnl.salesRevenue);
  if (sales <= 0) return 0;
  // Quote-part buyers finance the pure Build contract. The promoter's reserve is
  // not a buyer construction bill and therefore stays in SPV economics.
  const constructionBasis = Math.max(0, pnl.constructionCost || 0);
  return Math.min(1, Math.max(0, constructionBasis / sales));
}

/** Land share of sale price (quote-part terrain transfer). */
export function landRatio(pnl) {
  const sales = Math.max(0, pnl.salesRevenue);
  if (sales <= 0) return 0;
  return Math.min(1, Math.max(0, pnl.totalLandCost / sales));
}

/**
 * Split one sale tranche into mutually exclusive buyer-funding destinations.
 * Pure function — easy to unit-test conservation.
 */
export function splitBuyerTransaction({
  revenueDelta,
  salesDelta,
  pnl,
  constructionPct,
  spvAdvancedConstruction,
  prevSoldShare,
}) {
  const revenue = Math.max(0, n(revenueDelta));
  const newlySold = Math.max(0, n(salesDelta));
  const cRatio = pureConstructionRatio(pnl);
  const lRatio = landRatio(pnl);

  // Keep land + construction shares from overlapping: land first from residual after construction.
  const constructionShare = revenue * cRatio;
  const landShare = Math.min(revenue - constructionShare, revenue * lRatio);
  const usableSpvProceeds = Math.max(0, revenue - constructionShare - landShare);

  // Prior SPV advances attributable to the newly sold share of remaining unsold stock.
  const advancedPool = Math.max(0, n(spvAdvancedConstruction));
  const unsoldBefore = Math.max(0, 1 - Math.max(0, n(prevSoldShare)));
  const advancedOnNewlySold = newlySold > 0 && unsoldBefore > 0
    ? advancedPool * Math.min(1, newlySold / unsoldBefore)
    : 0;

  // Reimbursement cannot exceed construction share of this tranche.
  // Built fraction of that construction share is also capped by current construction %.
  const builtCap = constructionShare * Math.min(1, Math.max(0, n(constructionPct)));
  const constructionReimbursement = Math.min(constructionShare, advancedOnNewlySold, builtCap);
  const buyerDirectCurrentConstruction = Math.max(0, constructionShare - constructionReimbursement);
  const buyerDirectLandSettlement = Math.max(0, landShare);

  return {
    revenueDelta: revenue,
    usableSpvProceeds,
    buyerDirectLandSettlement,
    buyerDirectCurrentConstruction,
    constructionReimbursement,
    // Convenience: cash that actually enters the SPV bank account from this tranche.
    usableBuyerCash: usableSpvProceeds + constructionReimbursement,
  };
}

export function quotePartMonthDecision(ctx) {
  const {
    month,
    programme,
    pnl,
    assumptions,
    salesRow,
    costs,
    state,
  } = ctx;

  const facilities = resolveSecuredFacilities(pnl, assumptions);
  const salesPct = salesRow.salesPct;
  const constructionPct = constructionPctAtMonth(month, programme, ctx.costSchedule);
  const newlySold = Math.max(0, salesRow.salesDelta);
  const prevSoldShare = Math.max(0, salesPct - newlySold);

  const split = splitBuyerTransaction({
    revenueDelta: salesRow.revenueDelta,
    salesDelta: newlySold,
    pnl,
    constructionPct,
    spvAdvancedConstruction: state.spvAdvancedConstruction,
    prevSoldShare,
  });

  // Gross construction this month. Sold share is buyer-direct (not an SPV cost).
  const grossConstructionNow = costs.detail.constructionCost || 0;
  const reserveNow = costs.detail.constructionReserve || 0;
  const soldShare = salesPct;
  const spvConstructionCost = Math.max(0, grossConstructionNow * (1 - soldShare));
  const constructionCostRelief = Math.max(0, grossConstructionNow - spvConstructionCost);

  const landCostNow =
    (costs.detail.landPrice || 0) + (costs.detail.acquisitionCosts || 0);
  // Reserve remains an SPV cash cost even after units are sold.
  const constructionCostNow = spvConstructionCost + reserveNow;

  const equityInjected = pnl.sponsorEquity + state.cumulativeSpvContributions;
  const sharedCap = facilities.sharedBankFacility;
  const landCap = facilities.landSecured;
  const constructionCap = facilities.constructionSecured;
  const landDrawnToDate = state.landFacilityDrawn || 0;
  const sharedDrawnToDate = state.sharedFacilityDrawn || 0;
  const securedConstructionDrawnToDate = state.securedConstructionFacilityDrawn || 0;
  const effectiveSpvCost = Math.max(0, costs.total - constructionCostRelief);

  const unmet = [];
  let bankDraw = 0;
  let landFacilityDraw = 0;
  let constructionFacilityDraw = 0;
  let sharedFacilityDraw = 0;
  let securedConstructionFacilityDraw = 0;

  if (landCostNow > 1e-9) {
    const sharedHeadroom = Math.max(0, sharedCap - sharedDrawnToDate);
    const dedicatedLandHeadroom = Math.max(0, landCap - landDrawnToDate);
    const landHeadroom = sharedHeadroom + dedicatedLandHeadroom;
    const check = evaluateLandBankConditions({
      month,
      programme,
      facilitySecured: landHeadroom,
      eligibleCostsIncurred: landCostNow + state.cumulativeLandCosts,
      drawnToDate: 0,
      requestedDraw: landCostNow,
      landRequiresRentalSelfSufficiency: assumptions.landRequiresRentalSelfSufficiency,
      rentalSelfSufficient: assumptions.rentalSelfSufficient,
    });
    if (check.met) {
      const draw = Math.min(landCostNow, landHeadroom);
      bankDraw += draw;
      landFacilityDraw += draw;
      sharedFacilityDraw += Math.min(draw, sharedHeadroom);
    } else {
      for (const u of check.unmet) unmet.push(`Land facility: ${u}`);
    }
  }

  const landExcessExpectedInSpv = Math.max(
    0,
    split.buyerDirectLandSettlement - (state.bankDebtOutstanding + bankDraw),
  );

  const constructionFundingNeed = Math.max(
    0,
    Math.min(
      constructionCostNow,
      assumptions.minCashBuffer + effectiveSpvCost
        - (n(ctx.availableSpvCash) + bankDraw + split.usableBuyerCash + landExcessExpectedInSpv),
    ),
  );
  if (constructionFundingNeed > 1e-9) {
    const sharedHeadroom = Math.max(
      0,
      sharedCap - sharedDrawnToDate - sharedFacilityDraw,
    );
    const securedHeadroom = Math.max(
      0,
      constructionCap - securedConstructionDrawnToDate,
    );
    const constructionHeadroom = sharedHeadroom + securedHeadroom;
    const check = evaluateConstructionBankConditions({
      month,
      programme,
      salesPct,
      threshold: assumptions.bankActivationThreshold,
      sponsorEquityInjected: equityInjected,
      requiredSponsorEquity: pnl.sponsorEquity,
      facilitySecured: constructionHeadroom,
      eligibleCostsIncurred: constructionCostNow + state.cumulativeConstructionCosts,
      drawnToDate: 0,
      requestedDraw: constructionFundingNeed,
    });
    if (check.met) {
      const draw = Math.min(constructionFundingNeed, constructionHeadroom);
      bankDraw += draw;
      constructionFacilityDraw += draw;
      const fromShared = Math.min(draw, sharedHeadroom);
      sharedFacilityDraw += fromShared;
      securedConstructionFacilityDraw += Math.max(0, draw - fromShared);
    } else {
      for (const u of check.unmet) unmet.push(`Construction facility: ${u}`);
    }
  }

  let partnerDraw = 0;
  const partnerHeadroom = Math.max(0, facilities.privateSecured - state.partnerDrawn);
  const cashBeforePartner =
    n(ctx.availableSpvCash) +
    bankDraw +
    split.usableBuyerCash +
    landExcessExpectedInSpv -
    effectiveSpvCost;
  const uncovered = Math.max(0, assumptions.minCashBuffer - cashBeforePartner);
  if (partnerHeadroom > 0 && uncovered > 0) {
    partnerDraw = Math.min(partnerHeadroom, uncovered);
  }

  return {
    bankDraw,
    partnerDraw,
    landFacilityDraw,
    constructionFacilityDraw,
    sharedFacilityDraw,
    securedConstructionFacilityDraw,
    projectedConstructionFacilityDraw: 0,
    // SPV cash in from buyer = MEV/quote-part residual + construction reimbursement only.
    usableBuyerCash: split.usableBuyerCash,
    usableSpvProceeds: split.usableSpvProceeds,
    restrictedBuyerCashDelta: 0,
    buyerDirectConstruction: split.buyerDirectCurrentConstruction,
    buyerDirectLandSettlement: split.buyerDirectLandSettlement,
    spvCostRelief: constructionCostRelief,
    constructionReimbursement: split.constructionReimbursement,
    buyerTransaction: split,
    repayAtDelivery: month === programme.deliveryMonth,
    salesPct,
    constructionPct,
    unmetBankConditions: [...new Set(unmet)],
    facilities,
    landCostNow,
    constructionCostNow,
    pureConstructionRatio: pureConstructionRatio(pnl),
  };
}
